import {ArrUtil, Graph, Timeout} from 'yougile-util'
import {DataObjectId, Hub, HubLike, TaskDependencyData} from 'yougile-model'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {GanttDraft} from '@extensions/gantt-draft'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'

export class LinkCycleEncounteredError extends Error {}

export class CannotCreateLinkCycleError extends Error {}

type TaskLike = DataObjectId | HubLike | undefined

interface GanttData {
  linkedFrom: DataObjectId[]
  linksTo: DataObjectId[]
}

export class Gantt {
  private static index: Map<DataObjectId, GanttData> = new Map()
  private static subscriptions: (() => void)[] = []
  private static syncListener: number
  private static companyListener: number
  private static notifyHandle: Timeout | undefined

  static init(): void {
    const initForCurrent = () => {
      Gantt.buildIndex()
      if (this.syncListener) {
        Gantt.removeSyncListener()
      }
      Gantt.addSyncListener()
    }

    initForCurrent()

    this.companyListener = globals.onCompanyChange(initForCurrent)
  }

  static destroy(): void {
    this.removeSyncListener()
    globals.removeCompanyListener(this.companyListener)
    this.notifyHandle && clearTimeout(this.notifyHandle)
  }

  private static addSyncListener(): void {
    this.syncListener = SyncEvents.addListener(
      globals.getCompanyId(),
      '*',
      res => {
        for (const {companyId, ids} of res) {
          const box = Sync.get({companyId})
          for (const id of ids) {
            const toHub = box.getHub(id)
            if (toHub && box.getType(toHub) === 'task') {
              Gantt.updateIndexForHub(toHub)
            }
          }
        }
      }
    )
  }

  private static removeSyncListener(): void {
    SyncEvents.removeListener(this.syncListener)
  }

  private static buildIndex(): void {
    const hubs = Task.allTasksWithDeleted(globals.getCompanyId())
    const linksToMap: Map<DataObjectId, DataObjectId[]> = new Map()
    for (const toHub of hubs) {
      const dependencies = toHub.data.dependencies ?? {}
      const linkedFrom = Object.keys(dependencies) as DataObjectId[]
      if (linkedFrom.length) {
        const toData = Gantt.index.get(toHub.id) ?? {
          linkedFrom: [],
          linksTo: []
        }
        toData.linkedFrom = linkedFrom
        Gantt.index.set(toHub.id, toData)

        for (const linkedFromId of linkedFrom) {
          const linkedTo = linksToMap.get(linkedFromId) ?? []
          linkedTo.push(toHub.id)
          linksToMap.set(linkedFromId, linkedTo)
        }
      }
    }

    for (const [fromHubId, linksTo] of linksToMap.entries()) {
      const fromData = Gantt.index.get(fromHubId) ?? {
        linkedFrom: [],
        linksTo: []
      }
      fromData.linksTo = linksTo
      Gantt.index.set(fromHubId, fromData)
    }

    Gantt.scheduleNotify()
  }

  private static updateIndexForHub(toHub: Hub): void {
    const dependencies = toHub.data.dependencies ?? {}
    const linkedFrom = Object.keys(dependencies) as DataObjectId[]
    const oldToData = Gantt.index.get(toHub.id) ?? {
      linkedFrom: [],
      linksTo: []
    }
    if (!oldToData.linkedFrom.length && !linkedFrom.length) {
      return
    }

    const newToData: GanttData = {linkedFrom, linksTo: oldToData.linksTo}
    Gantt.index.set(toHub.id, newToData)

    const diff = ArrUtil.diff(oldToData.linkedFrom, newToData.linkedFrom)

    for (const fromHubId of diff.add) {
      const fromData = Gantt.index.get(fromHubId) ?? {
        linkedFrom: [],
        linksTo: []
      }
      fromData.linksTo.push(toHub.id)
      Gantt.index.set(fromHubId, fromData)
    }

    for (const fromHubId of diff.remove) {
      const fromData = Gantt.index.get(fromHubId) ?? {
        linkedFrom: [],
        linksTo: []
      }
      fromData.linksTo = fromData.linksTo.filter(id => id !== toHub.id)
      Gantt.index.set(fromHubId, fromData)
    }

    Gantt.scheduleNotify()
  }

  static subscribe(update: () => void): () => void {
    if (!Gantt.subscriptions.includes(update)) {
      Gantt.subscriptions.push(update)
    }

    return () => {
      Gantt.subscriptions = Gantt.subscriptions.filter(u => u !== update)
    }
  }

  private static scheduleNotify(): void {
    if (!this.notifyHandle) {
      this.notifyHandle = setTimeout(() => {
        this.notifyHandle = undefined
        for (const update of Gantt.subscriptions) {
          update()
        }
      })
    }
  }

  static getLinkedFromHubs(task: TaskLike): ReadonlyArray<Hub> {
    const toHub = Gantt.getTask(task)
    if (!toHub) {
      return []
    }

    const data = Gantt.index.get(toHub.id)
    const linkedFrom = data?.linkedFrom ?? []

    const box = Sync.getCurrentSandbox()
    const result: Hub[] = []
    for (const id of linkedFrom) {
      const hub = box.getHub(id)
      if (box.isDeleted(id) || !hub || Task.isArchived(hub)) {
        continue
      }
      result.push(hub)
    }

    return result
  }

  static getLinksToHubs(task: TaskLike): ReadonlyArray<Hub> {
    const toHub = Gantt.getTask(task)
    if (!toHub) {
      return []
    }

    const data = Gantt.index.get(toHub.id)
    const linksTo = data?.linksTo ?? []

    const result: Hub[] = []
    const box = Sync.getCurrentSandbox()
    for (const id of linksTo) {
      const hub = box.getHub(id)
      if (box.isDeleted(id) || !hub || Task.isArchived(hub)) {
        continue
      }
      result.push(hub)
    }

    return result
  }

  static relinkTaskDependencies(
    task: TaskLike,
    oldNewMap: Map<DataObjectId, DataObjectId>
  ): void {
    const hub = Gantt.getTask(task)
    if (!hub || !hub.data.dependencies) {
      return
    }

    const newDependencies: typeof hub.data.dependencies = {}
    for (const [oldKey, value] of Object.entries(hub.data.dependencies)) {
      const newKey = oldNewMap.get(oldKey as DataObjectId)
      newDependencies[newKey ?? oldKey] = value
    }
    hub.data.dependencies = newDependencies
    hub.commit()
  }

  /**
   * @throws LinkCycleEncounteredError
   * @throws CannotCreateLinkCycleError
   */
  static linkTasks(
    from: TaskLike,
    to: TaskLike,
    data: true | TaskDependencyData = true
  ): void {
    const fromHub = Gantt.getTask(from)
    const toHub = Gantt.getTask(to)
    if (!fromHub || !toHub) {
      return
    }

    Gantt.checkNoNewLinkCycles(fromHub, toHub)

    toHub.data.dependencies ??= {}
    toHub.data.dependencies[fromHub.id] = data
    toHub.commit()

    this.updateIndexForHub(toHub)

    GanttDraft.removeDraft(toHub.id)
    GanttDraft.planTaskByDependencies(toHub.id)
    GanttDraft.planDependentTasks(toHub.id)
  }

  static unlinkTasks(from: TaskLike, to: TaskLike): void {
    const fromHub = Gantt.getTask(from)
    const toHub = Gantt.getTask(to)
    if (!fromHub || !toHub) {
      return
    }

    toHub.data.dependencies ??= {}
    delete toHub.data.dependencies[fromHub.id]
    toHub.commit()

    this.updateIndexForHub(toHub)

    GanttDraft.removeDraft(toHub.id)
    GanttDraft.planTaskByDependencies(toHub.id)
    GanttDraft.planDependentTasks(toHub.id)
  }

  private static getTask(obj: TaskLike): Hub | undefined {
    if (typeof obj === 'string') {
      const box = Sync.getCurrentSandbox()
      const hub = box.getHub(obj)
      return hub && box.getType(hub) === 'task' ? hub : undefined
    } else if (obj) {
      const box = Sync.get(obj)
      const hub = box.getHub(obj.id)
      return hub && box.getType(hub) === 'task' ? hub : undefined
    }
    return undefined
  }

  /**
   * @throws LinkCycleEncounteredError
   * @throws CannotCreateLinkCycleError
   */
  private static checkNoNewLinkCycles(fromHub: Hub, toHub: Hub) {
    const observedIds = new Set<DataObjectId>()
    Graph.dfs<Hub>({
      start: toHub,
      adjacent: Gantt.getLinksToHubs,
      enter: hub => {
        // Encountered an already existing cycle
        if (observedIds.has(hub.id)) {
          throw new LinkCycleEncounteredError()
        }
        // Some path from toHub goes to fromHub, and a new link creates a cycle
        if (hub.id === fromHub.id) {
          throw new CannotCreateLinkCycleError()
        }
        observedIds.add(hub.id)
        return true
      },
      leave: hub => observedIds.delete(hub.id)
    })
  }
}
